import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import TimelineTimeline from './TimelineTimeline';
import TimelineContent from './TimelineContent';
import { media, SCREEN_WIDTH } from '../../utils/media';
import { calcDecadeFromYear, getFirstSlideYearAndId, getSlidesVisibleYear, getTimelineSlides, getYearsFlat, setTimelineYearsObject, setHighlightClassMod, useMobile, getClientHeight, } from './Timeline._helpers';
import { isSafari } from '../../utils/browsers';
import { isMobileOnly, isTablet } from 'react-device-detect';
const highlightYearComponentHeight = 85;
const StyledBody = createGlobalStyle `
  body {
    height: auto !important;
  }
`;
const ContainerGrid = styled.section `
  display: flex;
  box-sizing: border-box;
  height: 100%;
  width: 100%;

  ${media.tablet} {
    position: fixed;
    left: 0;
    top: 50px;
  }

  ${media.mobile} {
    position: ${({ isSafari }) => (isSafari ? 'static' : 'fixed')};
    flex-direction: column-reverse;
    height: 100%; // ie
    height: ${({ safariIphoneTweak }) => {
    return safariIphoneTweak > 0 ? `${safariIphoneTweak}px` : 'calc(100vh - 50px)';
}};
  }
`;
const LandscapeOverlay = styled.div `
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 999;
  padding: 0 50px;

  ${`${media.mobile} and (orientation: landscape)`} {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;
const LandscapeText = styled.p `
  font-family: ${({ theme }) => theme.fontFamilyBase};
  font-style: normal;
  font-weight: normal;
  color: white;
  padding: 0px;
  font-size: 24px;
  text-align: center;
`;
const ContainerGridArea = styled.div `
  &.left {
    flex: 0 0 ${highlightYearComponentHeight}px;
    height: 100%;

    ${media.mobile} {
      height: ${highlightYearComponentHeight}px;
      overflow-y: hidden;
      background-color: ${({ theme }) => theme.primaryWhite};
    }
  }

  &.right {
    height: 100%;

    ${media.mobile} {
      height: 84%; // ie
      height: 100%;
      height: calc(100% - ${highlightYearComponentHeight}px);
    }
    flex: auto;
  }
`;
const Timeline = ({ fields, params }) => {
    if (!fields || !fields.items) {
        return null;
    }
    const [containerHeight, setContainerHeight] = useState(0);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            setContainerHeight(window.innerHeight - 50);
        }
    });
    // vars, states
    const enCA = 'en';
    const currentLanguage = params && params['currentLanguage'] ? params['currentLanguage'] : enCA;
    let breakCycle = 0;
    // states   @todo useReducer if applicable
    const [contentWidth, setContentWidth] = useState(0);
    const [contentHeight, setContentHeight] = useState(0);
    const [setYear, setSetYear] = useState(0); // main year, set via scroll
    const [setDecade, setSetDecade] = useState(0); // main decade
    const [setId, setSetId] = useState(''); // clicked id, set via click
    const [clickYear, setClickYear] = useState({
        // clicked year, main set after scroll
        year: 0,
        id: '',
        doScrollFinish: false,
    });
    const [setLang, setSetLang] = useState(enCA);
    const [hasMobileIOSLoaded, setHasMobileIOSLoaded] = useState(false);
    // methods
    const yearsFromData = useMemo(() => {
        const fieldItems = setTimelineYearsObject(fields.items);
        getYearsFlat(fieldItems);
        return fieldItems;
    }, [fields.items]);
    const initYear = useMemo(() => getFirstSlideYearAndId(yearsFromData), [yearsFromData]);
    const setSlidesContentHeight = useCallback((width, height) => {
        setContentWidth(width);
        setContentHeight(height);
    }, []);
    const setDecadeGroupOnClick = useCallback((decade) => {
        if (decade === setDecade) {
            decade = 0;
        } // undo decade for plus / minus
        setSetDecade(decade);
    }, [setDecade]);
    const setHighlightYearOnClickMobile = useCallback((clickYr, clickId) => {
        setSetYear(clickYr);
        setSetId(clickId);
        const decade = calcDecadeFromYear(clickYr);
        setSetDecade(decade);
        setClickYear({ year: clickYr, id: clickId, doScrollFinish: true });
        setHighlightClassMod({ year: clickYr, id: clickId }); /* @todo  temp. fix */
    }, []);
    const setHighlightYearOnClickDesktop = useCallback((clickYr, clickId) => {
        setSetYear(clickYr);
        setSetId(clickId);
        const decade = calcDecadeFromYear(clickYr);
        setSetDecade(decade);
        setClickYear({ year: clickYr, id: clickId, doScrollFinish: true });
        setHighlightClassMod({ year: clickYr, id: clickId });
    }, []);
    const setHighlightYearOnClick = useCallback((clickYr, clickId) => {
        setClickYear({ year: clickYr, id: clickId, doScrollFinish: true });
        setSetId(clickId);
    }, []);
    const setHighlightYearOnScroll = useCallback((isUp = false) => {
        const yearHL = getSlidesVisibleYear(getTimelineSlides(), contentHeight, isUp);
        if (yearHL.year && yearHL.id) {
            setSetYear(yearHL.year);
            setSetId(yearHL.id);
            const decade = calcDecadeFromYear(yearHL.year);
            setSetDecade(decade);
            // desktop animation
            const isDesktop = !useMobile(0, SCREEN_WIDTH.SMALL_TABLET);
            if (isDesktop) {
                // click triggers scroll, scroll sets highlight, *but* scroll can also trigger without click by user
                if (breakCycle) {
                    setHighlightClassMod(yearHL); /* @todo  temp. fix */
                    breakCycle = 0;
                }
                else {
                    setHighlightYearOnClick(yearHL.year, yearHL.id);
                    setHighlightClassMod(yearHL); /* @todo  temp. fix */
                    breakCycle = 1;
                }
            }
        }
    }, [contentHeight]);
    // after render
    useEffect(() => {
        if (initYear) {
            setSetLang(currentLanguage);
            setSetDecade(calcDecadeFromYear(initYear.year));
            setClickYear({ year: initYear.year, id: initYear.id, doScrollFinish: false });
            setSetYear(initYear.year);
            setSetId(initYear.id);
            setTimeout(() => setHighlightClassMod(initYear), 500);
            return;
        }
        // field, fields.items required
        console.log('****************************************************');
        console.log(' Timeline component requires fields to be provided. ');
        console.log('****************************************************');
    }, [fields.items]);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            // desktop only
            if (!isTablet && !isMobileOnly) {
                window.document.body.style.position = 'fixed';
                window.document.body.style.width = '100%';
                // mobile only
            }
            else if (isMobileOnly) {
                setHasMobileIOSLoaded(true);
            }
        }
        return () => {
            window.document.body.style.position = 'static';
            window.document.body.style.width = 'auto';
        };
    }, []);
    useEffect(() => {
        const IOSMobileAdujstment = 50;
        if (typeof window !== 'undefined' && hasMobileIOSLoaded) {
            const timelineSection = window.document.getElementById('timeline-section');
            const sectionHeight = timelineSection.clientHeight;
            timelineSection.style.height = `${sectionHeight - IOSMobileAdujstment}px`;
        }
    }, [hasMobileIOSLoaded]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ContainerGrid, { safariIphoneTweak: isSafari() && useMobile(0, SCREEN_WIDTH.MOBILE) ? getClientHeight() : 0, isSafari: isSafari(), id: "timeline-section", "aria-label": fields.ariaLabel && fields.ariaLabel.value },
            React.createElement(LandscapeOverlay, null,
                React.createElement(LandscapeText, null, "Please rotate your phone to experience Honda's Timeline.")),
            React.createElement(StyledBody, null),
            React.createElement(ContainerGridArea, { className: "left" },
                React.createElement(TimelineTimeline, { containerHeight: containerHeight, contentWidth: contentWidth, handleClickDecade: setDecadeGroupOnClick, handleClickArrow: setHighlightYearOnClickMobile, handleClick: setHighlightYearOnClickDesktop, setLang: setLang, setWidth: contentWidth, setDecade: setDecade, setYear: setYear, setId: setId, years: yearsFromData, clickYear: clickYear })),
            React.createElement(ContainerGridArea, { className: "right" },
                React.createElement(TimelineContent, { containerHeight: containerHeight, contentWidth: contentWidth, handleScroll: setHighlightYearOnScroll, handleHeight: setSlidesContentHeight, handleClick: setHighlightYearOnClick, clickYear: clickYear, years: yearsFromData, setYear: setYear, setId: setId, setHighlightYear: setHighlightYearOnClickMobile })))));
};
export default Timeline;
